import { ruleFromMetafields, caseDiscountMessage, tierForQuantity, currencyDigits, discountedUnit } from "./esp-case-pricing-core.js";

class EspQuantityPricing extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    try { this.data = JSON.parse(this.querySelector("[data-volume-data]").textContent); }
    catch { return; }
    this.hasPricingInteraction = false;
    this.lastAnnouncedKey = null;
    this.pendingPriceAnnouncement = null;
    this.scope = this.closest("product-form") || this.closest(".shopify-section") || document;
    this.onChange = event => {
      if (!event || event.type === 'popstate' || event.target?.matches?.('input[name="quantity"],input[name="selling_plan"],.appstle_subscription_radio,[data-esp-purchase],[data-esp-plan],.swt-input,input[name^="options"]')) this.hasPricingInteraction = true;
      this.render();
      requestAnimationFrame(() => { if (this.isConnected) this.render(); });
    };
    this.onVariantChange = event => {
      const id = event.detail?.variant?.id;
      if (id != null && !this.data.variants.some(variant => String(variant.id) === String(id))) return;
      this.onChange();
    };
    this.scope.addEventListener("change", this.onChange, { signal }); 
    this.scope.addEventListener("input", this.onChange, { signal });
    document.addEventListener("variant:change", this.onVariantChange, { signal });
    document.addEventListener("variant-change", this.onVariantChange, { signal });
    window.addEventListener("variant:change", this.onVariantChange, { signal });
    window.addEventListener("popstate", this.onChange, { signal });
    // Themes often replace the product form when a variant changes.
    this.observer = new MutationObserver(records => {
      if (records.some(r => !this.contains(r.target))) this.render();
    });
    this.observerOptions = { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ["value", "checked"] };
    this.observer.observe(this.scope, this.observerOptions);
    this.priceWrites = new Map();
    this.ruleId = `esp-case-rule-${this.data.productId}-${EspQuantityPricing.nextId = (EspQuantityPricing.nextId || 0) + 1}`;
    this.querySelector('[data-volume-content]').id = this.ruleId;
    this.render();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    clearTimeout(this.announcementTimer);
    this.pendingPriceAnnouncement = null;
    this.controller = null;
  }
  productForm() {
    const forms = [...this.scope.querySelectorAll('form[action*="/cart/add"]')];
    return forms.find(f => this.data.variants.some(v => String(v.id) === f.elements.namedItem("id")?.value)) ?? null;
  }
  currentVariant(form) {
    // ESP retains the last available form ID when a sold-out option is selected.
    // Its product-form element holds the actual selection and emits on window.
    if (this.scope.matches?.("product-form") && "currentVariant" in this.scope) {
      const selected = this.scope.currentVariant;
      if (!selected) return undefined;
      const variant = this.data.variants.find(v => String(v.id) === String(selected.id));
      return variant ? { ...variant, available: variant.available && selected.available !== false } : undefined;
    }
    const id = form?.elements.namedItem("id")?.value ?? new URL(location.href).searchParams.get("variant") ?? String(this.data.initialVariantId);
    return this.data.variants.find(v => String(v.id) === id);
  }
  quantityInput(form) {
    const controls = [...(form?.elements ?? [])].filter(e => e.matches('input[name="quantity"]'));
    return controls.find(e => e.getClientRects().length > 0)
      ?? controls.find(e => e.type !== "hidden" && !e.closest("[hidden]"));
  }
  selectedPlan(form) {
    return this.scope.querySelector('.appstle_subscription_wrapper input[name="selling_plan"]:checked')
      ?? form?.elements.namedItem("selling_plan");
  }
  writePrice(element, price, prefix = "") {
    if (!element) return;
    const moneySpans = [...element.querySelectorAll(".transcy-money")];
    const target = moneySpans.at(-1) ?? element;
    const text = target === element ? prefix + price : price;
    if (target.textContent === text) return;
    const prior = this.priceWrites.get(target);
    this.priceWrites.set(target, { original: prior?.original ?? target.textContent, written: text });
    target.textContent = text;
  }
  restorePrices() {
    for (const [target, value] of this.priceWrites) {
      if (target.isConnected && target.textContent === value.written) target.textContent = value.original;
    }
    this.priceWrites.clear();
  }
  renderPrices(variant, rule, form, plan) {
    if (!variant || !Number.isSafeInteger(variant.price)) { this.restorePrices(); return; }
    const main = this.scope.querySelector(".product-price .price");
    const controlsMainPrice = Boolean(rule) || [...this.priceWrites.keys()].some(target => main?.contains(target));
    const quantity = Number(this.quantityInput(form)?.value);
    const tier = variant.available && rule ? tierForQuantity(rule, quantity) : null;
    const digits = currencyDigits(this.data.currency);
    const minor = Math.round(variant.price / 100 * 10 ** digits);
    const price = new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency", currency: this.data.currency,
    }).format(discountedUnit(minor, tier?.percentBps ?? 0) / 10 ** digits);
    const oneTime = this.scope.querySelector(".appstle_one_time_price_wrapper");
    this.writePrice(oneTime?.querySelector(".appstle_subscription_amount"), price);
    this.writePrice(oneTime?.querySelector(".appstle_subscription_amount-appstle-sr-only"), price, "Sale price ");
    this.writePrice(this.scope.querySelector('[data-esp-regular-price]'), price);
    if (!controlsMainPrice) return;
    if (plan?.value) {
      const subscriptionPrice = this.scope.querySelector('[data-esp-sub-price]')?.textContent?.trim()
        || plan.closest?.(".appstle_subscription_wrapper_option")?.querySelector(".appstle_subscription_amount")?.textContent?.trim();
      if (subscriptionPrice) this.writePrice(main, subscriptionPrice);
      else {
        // Without an Appstle price source, return control to the subscription app.
        for (const [target, value] of this.priceWrites) {
          if (main?.contains(target) && target.textContent === value.written) {
            target.textContent = value.original;
            this.priceWrites.delete(target);
          }
        }
      }
    } else this.writePrice(main, price);
  }
  queuePriceAnnouncement(state) {
    if (!this.hasPricingInteraction) {
      this.lastAnnouncedKey = state?.key ?? null;
      return;
    }
    if (!state || state.key === this.lastAnnouncedKey) {
      clearTimeout(this.announcementTimer);
      this.pendingPriceAnnouncement = null;
      return;
    }
    if (state.key === this.pendingPriceAnnouncement?.key) return;
    clearTimeout(this.announcementTimer);
    this.pendingPriceAnnouncement = state;
    this.announcementTimer = setTimeout(() => {
      if (!this.isConnected || this.pendingPriceAnnouncement?.key !== state.key) return;
      const status = this.querySelector('[data-volume-status]');
      if (status && status.textContent !== state.message) status.textContent = state.message;
      this.lastAnnouncedKey = state.key;
      this.pendingPriceAnnouncement = null;
    }, 150);
  }
  render() {
    const form = this.productForm();
    const variant = this.currentVariant(form);
    const plan = this.selectedPlan(form);
    const subscription = Boolean(plan?.value);
    const rule = ruleFromMetafields(String(variant?.minimum ?? ""), String(variant?.percent ?? ""));
    const content = this.querySelector("[data-volume-content]");
    const visible = Boolean(form && variant?.available && rule && !subscription);
    content.hidden = !visible;
    const note = this.querySelector("[data-volume-editor-note]");
    if (note) note.hidden = visible;
    if (visible) this.querySelector("[data-volume-message]").textContent = caseDiscountMessage(rule);
    const quantityInput = this.quantityInput(form);
    if (quantityInput) {
      const descriptions = new Set((quantityInput.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      if (visible) descriptions.add(this.ruleId); else descriptions.delete(this.ruleId);
      if (descriptions.size) quantityInput.setAttribute('aria-describedby', [...descriptions].join(' '));
      else quantityInput.removeAttribute('aria-describedby');
    }
    // Only displayed text changes. Catalog values and subscription inputs remain
    // the original values, so Appstle cannot compound the case percentage.
    this.observer.disconnect();
    try {
      this.renderPrices(variant, rule, form, plan);
      const price = this.scope.querySelector('.product-price .price')?.textContent.trim();
      const tier = visible ? tierForQuantity(rule, Number(quantityInput?.value)) : null;
      if (price && variant?.available) {
        const key = [variant.id, plan?.value || '', price, tier?.percentBps || 0].join('|');
        const messages = window.espAccessibilityStrings || {};
        const message = subscription ? messages.subscriptionPrice || 'Subscription unit price: {price}.' : tier ? messages.casePrice || 'Unit price: {price}. Case discount applied.' : messages.unitPrice || 'Unit price: {price}.';
        this.queuePriceAnnouncement({ key, message: message.replace('{price}', price) });
      } else this.queuePriceAnnouncement(null);
    }
    finally { this.observer.observe(this.scope, this.observerOptions); }
  }
}
if (!customElements.get("esp-quantity-pricing")) customElements.define("esp-quantity-pricing", EspQuantityPricing);
